// Dedicated, single-responsibility render atoms for the LLM-facing DSL
// reference. Every doc surface — the shared `<ExprDslReference>`, the
// Storybook/DOM render, and the plugin-chat reconciler — composes
// these, so an op renders identically everywhere. Element vocabulary
// is intentionally limited to `<dl>/<dt>/<dd>/<pre>` (+ `<h2>/<p>`) so
// both React DOM and the markdown reconciler produce a uniform shape
// with no special-casing.
//
// Pure entry extraction lives in `op-entry.ts`; this module exports
// only components.

import React, { Fragment, type ReactNode } from 'react';

import { exampleItems, type OpExample } from '../op-meta.ts';
import { type OpDocEntry, type OpDocScopeView, partitionTerse, signatureOf, type TerseGroupView } from './op-entry.ts';
import type { EnumDomainDoc } from './types.ts';
import type { KwargDoc } from './zod-walk.ts';

/**
 * Per-kwarg prose rows. Type + optionality already live in the
 * signature, so a row is emitted only when the kwarg carries a
 * non-trivial description — the novel semantics the signature can't
 * encode. No type echo, no "(optional)" boilerplate. A description
 * identical to the rendered type label (labeled codecs like `ColRef`
 * carry their label as the `describe` text) is a pure echo and is
 * dropped too.
 */
export function OpKwargs({ kwargs }: { readonly kwargs: readonly KwargDoc[] }): ReactNode {
  return kwargs
    .filter((k) => k.description !== undefined && k.description.trim() !== '' && k.description !== k.type)
    .map((k) => `  \`:${k.name}\` — ${k.description}\n`);
}

export function OpExampleView({ example }: { readonly example: OpExample }): ReactNode {
  return exampleItems(example).map((it) => (
    <React.Fragment key={it.expr}>
      <code>{it.expr.replaceAll('\n', ' ')}</code>
      {!!it.result && <> → {it.result}</>}
    </React.Fragment>
  ));
}

/**
 * One op: a `<dt>` (name + effect) followed by a uniform sequence of
 * `<dd>` blocks (signature, description, optional type-note, documented
 * kwargs, examples). Always the same shape — no compact/expanded fork.
 */
export function OpEntry({ entry }: { readonly entry: OpDocEntry }): ReactNode {
  return (
    <li>
      {signatureOf(entry)}
      {entry.description ? ` — ${entry.description}` : ''}
      {entry.typeNote ? ` (${entry.typeNote})` : ''}
      <OpKwargs kwargs={entry.kwargs} />
      {!!entry.example && <OpExampleView example={entry.example} />}
    </li>
  );
}

/**
 * One collapsed line for a terse group: heading, the shared blurb (the
 * semantics that would otherwise be copy-pasted per op), the full
 * member-name list, and one worked example. Replaces N near-identical
 * `<OpEntry>` rows for ops whose behaviour is obvious from name +
 * arity.
 */
export function TerseOpGroup({ view }: { readonly view: TerseGroupView }): ReactNode {
  return (
    <li>
      {view.def.heading} — {view.def.blurb} Ops:{' '}
      {view.names.map((n, i) => (
        <Fragment key={n}>
          {i > 0 ? ', ' : ''}
          <code>{n}</code>
        </Fragment>
      ))}
      . Example: <code>{view.def.example}</code>
    </li>
  );
}

export function OpDocTable({ name, description, entries }: OpDocScopeView): ReactNode {
  const { groups, rest } = partitionTerse(entries);
  return (
    <>
      <h2>{name}</h2>
      {description !== undefined && <p>{description}</p>}
      <ul>
        {groups.map((g) => (
          <TerseOpGroup key={g.id} view={g} />
        ))}
        {rest.map((e) => (
          <OpEntry entry={e} key={e.name} />
        ))}
      </ul>
    </>
  );
}

/**
 * The single place enum values are spelled out. Signatures reference a
 * domain by name (`:color Color`); this glossary expands each domain
 * once — with per-member example docs when the codec supplies them.
 */
export function EnumDomains({ domains }: { readonly domains: readonly EnumDomainDoc[] }): ReactNode {
  return (
    <>
      <h2>Enum domains</h2>
      <p>Legal values for the symbol-valued kwargs above (referenced by domain name in signatures).</p>
      <dl>
        {domains.map((d) => (
          <Fragment key={d.name}>
            <dt>
              <code>{d.name}</code>
            </dt>
            <dd>
              {d.valueDocs === undefined
                ? d.typeLabel
                : Object.entries(d.valueDocs)
                    .map(([member, doc]) => `${member} (${doc})`)
                    .join(' | ')}
            </dd>
          </Fragment>
        ))}
      </dl>
    </>
  );
}
